import { useImmerReducer } from "use-immer";
import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../store";
import { useFutureCostQueries } from "./useFutureCostQueries";
import { useDebounce } from "use-debounce";
import SimpleReactValidator from "simple-react-validator";
import dayjs from "dayjs";
import { formatDate } from "../../../utils";
import { InputControl } from "../../../shared";
import { addDays } from "date-fns";

// Inclusive and exclusive GST rules and messages
export const futureCostValidationRules = {
  rules: "required|numeric|min:0,num",
  messages: {
    messages: {
      required: "Cost is required",
      numeric: "Cost be a valid number",
      min: "Cost cannot be less than 0",
    },
  },
};

// Reducer function to manage local state
const reducer = (draft, action) => {
  switch (action.type) {
    case "SET_DIALOG":
      {
        const { name, value } = action.payload;
        draft.dialog[name] = value;
      }
      break;
    case "SET_SELECTED_ROWS":
      draft.selectedRows = action.payload;
      break;
    case "UPDATE_ROW": {
      const updatedForm = { ...draft.formData, ...action.payload };
      draft.formData = updatedForm;
      return;
    }
    case "SET_EDIT_ROW":
      {
        const {
          id,
          ingredient_id,
          future_cost_exc_gst,
          costing_date,
          future_cost_inc_gst,
        } = action.payload;
        draft.formData = {
          id,
          ingredient_id,
          future_cost_exc_gst,
          future_cost_inc_gst,
          costing_date,
        };
      }
      break;
    case "RESET_EDIT_ROW":
      draft.formData = initialFormData;
      break;
    case "SET_FILTER":
      draft.filter[action.payload.name] = action.payload.value;
      break;
    case "REST_ROW_SELECTION":
      if (draft.selectedRows.length > 0)
        draft.resetRowSelection = !draft.resetRowSelection;
      else draft.resetRowSelection = null;
      break;
    default:
      break;
  }
};

const initialFormData = {
  id: null,
  costing_date: null,
  future_cost_exc_gst: "",
  future_cost_inc_gst: "",
  ingredient_id: null,
};

export const useFutureCostList = () => {
  // Setting up reducer for local state management
  const [state, dispatch] = useImmerReducer(reducer, {
    dialog: { delete: false, import: false },
    selectedRows: [],
    resetRowSelection: null,
    formData: initialFormData,
    filter: { status: null, type: [], date: {} },
  });
  const [_, forceUpdate] = useState(0);

  // Accessing global app context
  const {
    setTableState,
    tableData,
    clearAllFilters,
    setSorting,
    setPagination,
    setSearchState,
    globalData,
  } = useContext(AppContext);

  // Module name for future costs
  const moduleName = "futureCosts";

  // Current table state
  const tableState = tableData[moduleName];

  // Debounced search value
  const [debouncedSearch] = useDebounce(tableState.search, 1000);

  // Function to get list params based on table state
  const getListParams = (state) => {
    const params = {};
    // Apply pagination
    if (state.pagination) {
      const { pageIndex, pageSize } = state.pagination;
      params.page = pageIndex;
      params.per_page = pageSize;
    }

    // Apply search filter
    if (state?.search) params.search = state.search;

    // Apply sorting filter
    if (state?.sorting?.length > 0) {
      const sortByParams = state.sorting.reduce(
        (obj, item) =>
          Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
        {},
      );
      params.sort = sortByParams;
    }

    // Apply type filter
    if (state?.data?.type?.length > 0) {
      params.ingredient_type = state.data.type.map((t) => t.value);
    }

    if (state?.data?.status) {
      params.status = state.data.status.value;
    }

    if (state?.data?.date?.length > 0) {
      const { startDate, endDate } = state?.data?.date[0];
      params.from_date = formatDate(startDate);
      params.to_date = formatDate(endDate);
    }

    return params;
  };

  // Fetching future cost list
  const {
    listQuery,
    deleteFutureCostQuery,
    downloadTemplateQuery: {
      mutate: downloadTemplateAsync,
      isPending: isDownloading,
    },
    updateFutureQuery: { data, mutate: updateAsync, isPending: isUpdating },
    importTemplateQuery,
  } = useFutureCostQueries({
    list: true,
    listSearch: debouncedSearch,
    getListParamsCallback: getListParams.bind(null, {
      ...tableState,
      search: debouncedSearch,
    }),
  });

  // Reset row selection
  useEffect(() => {
    if (listQuery.isSuccess && listQuery.data) {
      dispatch({ type: "REST_ROW_SELECTION", payload: listQuery.data });
    }
  }, [listQuery.data]);

  // Setting up form validator
  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate },
      element: (message) => message,
      validators: {
        notPastDate: {
          message: "Date must be in the future.",
          rule: (val) => {
            if (!val) return true;

            const selected = dayjs(val);
            const today = dayjs().startOf("day");

            if (!selected.isValid()) return false;

            return selected.isAfter(today, "day");
          },
          required: false,
        },
      },
    }),
  );

  // Format payload for bulk delete
  const formatDeletePayload = (rows) => {
    return {
      dates: rows.map((row) => ({
        costing_date: row.costing_date,
        ingredients: [
          {
            ingredient_id: row.ingredient_id,
          },
        ],
      })),
    };
  };

  // Handler for bulk delete action
  const onBulkDelete = async (rows) => {
    await deleteFutureCostQuery.mutateAsync(formatDeletePayload(rows));
  };

  // Handler for input changes in the table
  const onChange = (event, row = null) => {
    const { name, value } = event.target;
    let updates = {};

    if (name === "costing_date") {
      updates.costing_date = value;
    }

    const IsIncGst = name.startsWith("future_cost_inc_gst");
    const IsExcGst = name.startsWith("future_cost_exc_gst");

    if (IsIncGst || IsExcGst) {
      const rowTaxRate = row?.tax_rate;
      if (typeof rowTaxRate !== "number") return;

      const taxRate = rowTaxRate / 100;
      const numericValue = Number(value) || 0;

      if (IsExcGst) {
        updates.future_cost_exc_gst = value;
        updates.future_cost_inc_gst = (
          numericValue +
          numericValue * taxRate
        ).toFixed(5);
      }

      if (IsIncGst) {
        updates.future_cost_inc_gst = value;
        updates.future_cost_exc_gst = (numericValue / (1 + taxRate)).toFixed(5);
      }
    }

    dispatch({
      type: "UPDATE_ROW",
      payload: updates,
    });
  };

  // Handler for search input changes
  const onSearchHandler = (search) => {
    setSearchState({
      tableName: moduleName,
      key: "search",
      value: search,
    });
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1, // Reset to the first page
      }),
    });
  };

  const onFilterChange = (event) => {
    const { name, value } = event.target;
    console.log(name, value);
    dispatch({ type: "SET_FILTER", payload: { name, value } });
  };

  // Function to get editable row components
  const getEditRow = ({ row, columnId, tableRow }) => {
    switch (columnId) {
      case "costing_date": {
        const value = dayjs(row.costing_date).format("DD MMM YYYY");
        const name = "date";

        const dateHelperText = formValidator.current.message(
          name,
          value,
          "required|notPastDate",
        );

        return (
          <InputControl
            type="datePicker"
            size="small"
            label=""
            name="date"
            helperText={dateHelperText}
            error={!!dateHelperText}
            customDatePickerIcon
            datePickerProps={{
              disableMaskedInput: true,
              inputFormat: "dd MMM yyyy",
              minDate: addDays(new Date(), 1),
              value: value,
              onChange: (event) =>
                onChange({ target: { name: "costing_date", value: event } }),
            }}
            sx={{
              width: "160px",
            }}
          />
        );
      }
      case "future_cost_exc_gst":
      case "future_cost_inc_gst": {
        const value = row[columnId];
        const name = columnId;
        const helperText = formValidator.current.message(
          name,
          value,
          futureCostValidationRules.rules,
          futureCostValidationRules.messages,
        );

        return (
          <InputControl
            name={name}
            size="small"
            type="number"
            value={value}
            title={value}
            onChange={(event) => onChange(event, tableRow)}
            error={!!helperText}
            helperText={helperText}
            sx={{ width: "120px" }}
          />
        );
      }
      default:
        return null;
    }
  };

  const onSaveChanges = async (event) => {
    event.preventDefault();
    if (formValidator.current.allValid()) {
      const { id, ...payload } = state.formData;

      payload.costing_date = formatDate(payload.costing_date);

      updateAsync({ id, payload });
    } else {
      formValidator.current.showMessages();
      forceUpdate(_ + 1);
    }
  };
  const resetEdit = () => {
    dispatch({ type: "RESET_EDIT_ROW" });
  };

  // reset the edit form after success
  useEffect(() => {
    if (data?.success) resetEdit();
  }, [data]);

  return {
    moduleName,
    listQuery,
    isDownloading,
    state,
    isUpdating,
    globalData,
    tableState,
    formValidator,
    tableHelpers: {
      setPagination,
      setSorting,
      setSearchState,
      clearAllFilters,
      setTableState,
      onSearchHandler,
    },
    deleteFutureCostQuery,
    importTemplateQuery,
    resetEdit,
    onChange,
    dispatch,
    onSaveChanges,
    onBulkDelete,
    onFilterChange,
    getEditRow,
    downloadTemplateAsync,
  };
};
